import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import {
  confirmBulkUpload,
  getBulkUploadDetail,
  requestBulkUploadUrl,
  startBulkUploadProcessing,
} from '../../../services/queries/BulkDealDocsQueries';
import { invalidateBulkDealDocsData } from './BulkDealDocsUpload';
import { selectedBulkDealDocsActionState, selectedBulkUploadIdState } from './bulkDealDocsState';
import { BULK_UPLOAD_CONFIG, FOLDER_STRUCTURE_EXAMPLE, SUPPORTED_DOCUMENT_TYPES } from './constants';
import { BulkUploadLogger } from './logging';
import { usePolling } from './usePolling';
import { formatFileSize, uploadToS3WithRetry, validateUploadFile } from './utils';

export function UploadBulkDealDocsModal() {
  const [selectedAction, setSelectedAction] = useAtom(selectedBulkDealDocsActionState);
  const setInvalidate = useSetAtom(invalidateBulkDealDocsData);
  const setSelectedUploadId = useSetAtom(selectedBulkUploadIdState);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string | null>(null);
  const [uploadId, setUploadId] = useState<number | null>(null);

  const open = selectedAction === 'upload';

  // Use polling hook for status updates
  const { stop: stopPolling } = usePolling({
    pollFn: async () => {
      if (!uploadId) {
        throw new Error('No upload ID');
      }
      return getBulkUploadDetail(uploadId);
    },
    onSuccess: (detail) => {
      BulkUploadLogger.pollStatus(uploadId!, detail.status);
      setProcessingStatus(detail.status);

      if (detail.status === 'completed' || detail.status === 'error') {
        setSuccess(detail.status === 'completed');
        if (detail.status === 'error') {
          setError(detail.errorMessage || 'Processing failed');
        }
        // Refresh data after a short delay
        setTimeout(() => {
          setInvalidate((prev) => prev + 1);
        }, 1000);
      }
    },
    onError: (err) => {
      BulkUploadLogger.error('Failed to poll upload status', err, { uploadId });
    },
    shouldContinue: (detail) => {
      if (!detail) {
        return true;
      }
      return detail.status !== 'completed' && detail.status !== 'error';
    },
    enabled:
      !!uploadId && !!processingStatus && processingStatus !== 'completed' && processingStatus !== 'error',
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const handleClose = () => {
    if (!uploading && !processingStatus) {
      stopPolling();
      setSelectedAction(null);
      setFile(null);
      setProgress(0);
      setError(null);
      setSuccess(false);
      setProcessingStatus(null);
      setUploadId(null);
    }
  };

  const handleViewDetails = () => {
    if (uploadId) {
      stopPolling();
      setSelectedUploadId(uploadId);
      setSelectedAction('view');
      // Clean up this modal's state
      setFile(null);
      setProgress(0);
      setError(null);
      setSuccess(false);
      setProcessingStatus(null);
      setUploadId(null);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Clear any existing errors immediately when user interacts with file input
    setError(null);

    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      const validation = validateUploadFile(selectedFile);
      if (!validation.valid) {
        setError(validation.error!);
        return;
      }

      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      return;
    }

    setUploading(true);
    setError(null);
    setProgress(10);

    try {
      // Step 1: Request presigned URL
      setProgress(20);
      BulkUploadLogger.info('Requesting upload URL', { fileName: file.name, fileSize: file.size });
      const { uploadUrl, s3Key } = await requestBulkUploadUrl(file.name, file.size);

      // Step 2: Upload to S3 with retry logic
      setProgress(40);
      BulkUploadLogger.info('Starting S3 upload', { fileName: file.name, s3Key });
      await uploadToS3WithRetry(uploadUrl, file);

      // Step 3: Confirm upload
      setProgress(70);
      BulkUploadLogger.info('Confirming upload', { fileName: file.name, s3Key });
      const { uploadId: newUploadId } = await confirmBulkUpload(file.name, file.size, s3Key);
      setUploadId(newUploadId);

      // Step 4: Start processing
      setProgress(90);
      BulkUploadLogger.info('Starting processing', { uploadId: newUploadId });
      await startBulkUploadProcessing(newUploadId);

      // Set processing status to start polling
      setProgress(100);
      setProcessingStatus('processing');
      setUploading(false);
      BulkUploadLogger.info('Upload complete, processing started', { uploadId: newUploadId });
    } catch (err) {
      BulkUploadLogger.error('Upload failed', err, { fileName: file.name });
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const fileInputId = 'bulk-upload-file-input';
  const progressPercentage = Math.round(progress);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography variant='h5'>Upload Bulk Deal Documents</Typography>
          <IconButton onClick={handleClose} disabled={uploading} aria-label='Close dialog'>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Alert severity='info'>
            Upload a ZIP file containing deal documents organized by Fund/Company/Round folder structure.
            Example: <code>Acme Fund/Widget Corp/20230115-Series-A/captable.xlsx</code>
          </Alert>

          {error && <Alert severity='error'>{error}</Alert>}
          {success && <Alert severity='success'>Processing completed successfully!</Alert>}
          {processingStatus && !success && !error && (
            <Alert severity='info'>
              Processing files... Status: <strong>{processingStatus}</strong>
              <br />
              This may take a few minutes depending on the number of files.
            </Alert>
          )}

          {!success && (
            <Box>
              <input
                type='file'
                accept={BULK_UPLOAD_CONFIG.ALLOWED_FILE_TYPE}
                onChange={handleFileSelect}
                disabled={uploading}
                style={{ display: 'none' }}
                id={fileInputId}
                aria-label='Select ZIP file for bulk upload'
                aria-describedby='file-requirements'
              />
              <label htmlFor={fileInputId}>
                <Button
                  variant='outlined'
                  component='span'
                  disabled={uploading}
                  fullWidth
                  sx={{ p: 2, textTransform: 'none' }}
                  aria-label={file ? `Selected file: ${file.name}` : 'Select ZIP file'}
                >
                  {file
                    ? file.name
                    : `Select ZIP File (max ${formatFileSize(BULK_UPLOAD_CONFIG.MAX_FILE_SIZE)})`}
                </Button>
              </label>

              {file && (
                <Typography variant='caption' display='block' mt={1} color='text.secondary'>
                  Size: {formatFileSize(file.size)}
                </Typography>
              )}
            </Box>
          )}

          {uploading && (
            <Box>
              <Typography variant='body2' color='text.secondary' mb={1} role='status' aria-live='polite'>
                Uploading and processing... {progressPercentage}%
              </Typography>
              <LinearProgress
                variant='determinate'
                value={progress}
                aria-label={`Upload progress: ${progressPercentage}%`}
              />
            </Box>
          )}

          <Box id='file-requirements'>
            <Typography variant='body2' color='text.secondary' gutterBottom>
              <strong>Supported file types:</strong>
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {SUPPORTED_DOCUMENT_TYPES.join(', ')}
            </Typography>
          </Box>

          <Box>
            <Typography variant='body2' color='text.secondary' gutterBottom>
              <strong>Expected folder structure:</strong>
            </Typography>
            <Typography variant='caption' component='pre' color='text.secondary'>
              {FOLDER_STRUCTURE_EXAMPLE}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading || (!!processingStatus && !success && !error)}>
          {success || error ? 'Close' : 'Cancel'}
        </Button>
        {(success || error) && uploadId && (
          <Button onClick={handleViewDetails} variant='outlined' color='primary'>
            View Details
          </Button>
        )}
        {!success && !error && (
          <Button
            onClick={handleUpload}
            variant='contained'
            color='primary'
            disabled={!file || uploading || !!processingStatus}
          >
            Upload & Process
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
